import Emitter from "./emitter";
import moment from "moment";
import { ILanguage, IDropDown } from "./interfaces";
import { allLanguages } from "../languages";
import { LanguageMessageItem } from "./batchNotificationInterface";
import {
	GTL_BASIC_USER,
	GTL_COUNTRY_ADMIN,
	GTL_COUNTRY_APPROVER,
	GTL_GLOBAL_ADMIN,
} from "./constants";
import DOMPurify from "dompurify";
import BatchMessageDetailModel from "../models/BatchMessage/BatchMessageDetailModel";
import { msalConfig, tokenRequest } from "../auth/authConfig";
import { PublicClientApplication } from "@azure/msal-browser";

export default class Shared {
	public static DateTimeFormate: string = "MM-DD-YYYY hh:mm";
	public static showSnackbarMessage(message: string) {
		Emitter.emit("showSnackbarMessage", message);
	}

	// currency formatter
	// locale (e.g. en-GB) determines formatting of the currency symbol (i.e. left or right of digits, etc.) no real way to adjust for each user
	public static currencyFormatter(currency?: string) {
		const formatter = new Intl.NumberFormat("en-GB", {
			style: "currency",
			currency: currency ? currency : "GBP",
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
		});
		return formatter;
	}
	public static currencyFormatterWithoutSymbol = new Intl.NumberFormat(
		"en-GB",
		{ style: "decimal", minimumFractionDigits: 0, maximumFractionDigits: 2 }
	);

	public static getFormatedCurrencyValue(
		value: number,
		currency?: string,
		isWithoutSymbol: boolean = false
	) {
		if (isWithoutSymbol) {
			return this.currencyFormatterWithoutSymbol.format(value);
		}
		return this.currencyFormatter(currency).format(value);
	}

	public static insertSpaceInEnumName(text: string) {
		// insert space before each capital letter in the word.
		// Trim is used to remove initial/first space added in word started with capital letter.
		return text.replace(/([A-Z])/g, " $1").trim();
	}
	public static oktaScopes(): string[] {
		var scopes = ["openid", "profile", "email", "groups"];
		if (process.env.REACT_APP_SCOPE) {
			scopes.push(process.env.REACT_APP_SCOPE);
		}
		return scopes;
	}

	public static async getAccessToken(): Promise<string> {
		const isClientIFRC = process.env.REACT_APP_CLIENT_NAME === "IFRC";

		if (isClientIFRC) {
			try {
				const tokenLocalStorage = JSON.parse(
					localStorage.getItem(
						`msal.token.keys.${process.env.REACT_APP_AZURE_CLIENT_ID}`
					) as string
				).accessToken?.[0];

				if (tokenLocalStorage) {
					return (
						JSON.parse(localStorage.getItem(tokenLocalStorage) as string)
							.secret || ""
					);
				} else {
					const msalInstance = new PublicClientApplication(msalConfig);
					const accounts = msalInstance.getAllAccounts();

					if (accounts.length === 0) {
						throw new Error("No accounts available.");
					}

					const account = accounts[0];
					const response = await msalInstance.acquireTokenSilent({
						...tokenRequest,
						account,
					});

					return response.accessToken;
				}
			} catch (error) {
				console.log("Error acquiring access token:", error);
				throw error;
			}
		} else {
			return (
				JSON.parse(localStorage.getItem("okta-token-storage") as string)
					.accessToken?.accessToken || ""
			);
		}
	}

	public static getUserInfoGroups() {
		var localStorageUserInfo = localStorage.getItem("okta-user-info");
		if (localStorageUserInfo) {
			var parsedUserInfo = JSON.parse(localStorageUserInfo);
			return parsedUserInfo?.groups ?? [];
		}
		return [];
	}

	public static formatDuration(seconds: number = 0) {
		let minutes = Math.floor(seconds / 60);
		seconds = seconds % 60;
		const hours = Math.floor(minutes / 60);
		minutes = minutes % 60;
		return (
			(hours > 0 ? ("0" + hours).slice(-2) + ":" : "") +
			("0" + minutes).slice(-2) +
			":" +
			("0" + seconds).slice(-2)
		);
	}

	public static SortData(data: any, columnName: string): any {
		if (data != null) {
			data.sort((a: any, b: any) => {
				let nameA = String(a[columnName]).toLowerCase();
				let nameB = String(b[columnName]).toLowerCase();

				if (nameA === "undefined") {
					nameA = "";
				}
				if (nameB === "undefined") {
					nameB = "";
				}
				// sort string ascending
				if (nameA < nameB) {
					return -1;
				}
				if (nameA > nameB) {
					return 1;
				}

				return -1; // default return value (no sorting)
			});
		}

		return data;
	}

	public static excelMimeTypeList = [".xlsx, .xls, .csv"];
	public static isBeforeCurrentDateTime(
		dateTime: Date | string,
		minDateTime: Date
	): boolean {
		return moment(dateTime, this.DateTimeFormate).isBefore(
			moment(minDateTime, this.DateTimeFormate)
		);
	}

	public static getFormattedDate(dateTime: Date | string): string {
		// remove AM/PM from the date and convert to mm/dd/yyyy hh:mm:ss format
		return new Date(dateTime).toLocaleString("en-US", { hour12: false });
	}

	public static getLanguageByCode(languageCode: string[]): ILanguage[] {
		const languageList: ILanguage[] = [];
		languageCode.forEach((code: string) => {
			const temp = allLanguages.find((x: any) => x.code === code);
			if (temp !== undefined) {
				languageList.push(temp);
			}
		});
		return languageList;
	}

	/* set access level by user role */
	// get user groups from local storage
	public static getUserGroups() {
		const isClientIFRC = process.env.REACT_APP_CLIENT_NAME === "IFRC";

		if (isClientIFRC) {
			const sessionObject = JSON.parse(
				localStorage.getItem("msal.account.keys") as string
			)[0];
			const groups = [];

			if (sessionObject) {
				const keysToCheck = [
					"extension_BasicUser",
					"extension_CountryAdmin",
					"extension_CountryApprover",
					"extension_globalAdmin",
				];

				for (const key of keysToCheck) {
					const value = JSON.parse(
						localStorage.getItem(sessionObject) as string
					).idTokenClaims[key];

					if (value) {
						groups.push(...value.split(","));
					}
				}
			}

			return groups;
		} else {
			const sessionObject = JSON.parse(
				localStorage.getItem("okta-token-storage") as string
			);

			if (
				sessionObject &&
				sessionObject.idToken &&
				sessionObject.idToken.claims
			) {
				return sessionObject.idToken.claims.groups;
			}

			return [];
		}
	}

	public static isGroupExists(
		groupName: string,
		isRequiredToCheckRegex: boolean = true
	) {
		const groups = Shared.getUserGroups();
		if (isRequiredToCheckRegex) {
			return groups && groups.length > 0
				? groups.filter((name: string) => {
						return name.match(new RegExp(groupName));
				  }).length > 0
				: false;
		}
		return groups && groups.length > 0 ? groups.includes(groupName) : false;
	}

	public static isUserRoleGlobalAdmin(): boolean {
		return this.isGroupExists(GTL_GLOBAL_ADMIN, false);
	}

	public static isUserRoleCountryAdmin(): boolean {
		return this.isGroupExists(GTL_COUNTRY_ADMIN);
	}

	public static isUserGlobalAdminOrCountryAdmin(): boolean {
		return this.isUserRoleGlobalAdmin() || this.isUserRoleCountryAdmin();
	}

	public static isBasicUser(): boolean {
		return this.isGroupExists(GTL_BASIC_USER);
	}

	public static isUserRoleCountryApprover(): boolean {
		return this.isGroupExists(GTL_COUNTRY_APPROVER);
	}

	public static canApproveOrRejectTemplate(): boolean {
		return (
			Shared.isUserGlobalAdminOrCountryAdmin() ||
			Shared.isUserRoleCountryApprover()
		);
	}
	/* END - set access level by user role */

	public static convertEnumToList(enumName: Object): IDropDown[] {
		const arrayObjects: IDropDown[] = [];
		for (const [propertyKey] of Object.entries(enumName)) {
			if (!Number.isNaN(Number(propertyKey))) {
				continue;
			}
			arrayObjects.push({
				type: propertyKey,
				description: this.insertSpaceInEnumName(propertyKey),
			});
		}
		return arrayObjects;
	}

	public static getSanitizedInputValue(value: string) {
		return DOMPurify.sanitize(value);
	}

	// Checks whether the message input contains '{{' and '}}' with no whitespace in between, suggesting
	// that the user has included a variable
	public static messageHasVariables = (
		item?: LanguageMessageItem,
		l?: any,
		message?: string
	) => {
		const regex = new RegExp(/{{\S+}}/);
		if (item) {
			const message = item.messages.find(
				(m: { languageCode: any }) => m.languageCode === l.code
			);
			if (message && regex.test(message?.message)) {
				return true;
			} else {
				return false;
			}
		} else if (message) {
			if (message.includes("{{") && message.includes("}}")) {
				return true;
			} else {
				return false;
			}
		} else {
			return false;
		}
	};

	// Checks whether the message input contains '[[' and ']]' with no whitespace in between, suggesting
	// that the user has included a dynamic variable
	public static messageDynamicVariables = (
		item?: LanguageMessageItem,
		language?: ILanguage,
		message?: string
	): string[] => {
		const regex = new RegExp(/\[\[\S+\]\]/);
		if (item) {
			const message = item.messages.find(
				(m: { languageCode: any }) => m.languageCode === language?.code
			);
			if (message) {
				const matches = regex.exec(message?.message);
				if (matches) {
					return [...matches];
				}
			}
		} else if (message) {
			const matches = message.match(regex);
			if (matches) {
				return [...matches];
			}
		}
		return [];
	};

	public static hasRecurringMessages = (
		batchNotification: BatchMessageDetailModel
	) => {
		batchNotification.batchMessageSchedulingDetail?.messages.forEach((msg) => {
			if (msg.isRecurring) {
				return true;
			}
		});
		return false;
	};
	public static whatsappBlockedCountries: string[] = [
		"Cuba",
		"Iran, Islamic Republic of",
		"Korea, Democratic People's Republic of",
		"Syrian Arab Republic",
	];

	public static whatsappRateLimit: number = 1000;
}

export const assertUnreachable = (x: never): never => {
	console.warn("Reached Unreachable Area");
	console.error(x);
	throw new Error("Didn't expect to get here");
};
